import React, { useState } from 'react';
import {
  Item,
  ItemDefinition,
  RenderProps,
  registry
} from '~/training/sequence/templates/items/Item';
import { CARD_FORM_PLACEHOLDER } from '~/training/sequence/templates/items/itemIds';
import { useEditorStore } from '~/shell/Store';
import { AddCardOutline } from '~/training/sequence/templates/items/add-card/AddCardOutline';
import { CardModal } from './CardModal';
import { getAddCardDisabledState } from './utils';
import { CardTemplate } from './templates/types';
import { GLOBAL_PLACEHOLDER } from './templates/constants';

interface Props {
  sequenceId: string | number;
  text?: string;
  width?: string;
  height?: string;
  maxCards?: number | null;
  cardTemplate: CardTemplate;
}

export type CardFormPlaceholderDefinition = ItemDefinition<
  typeof CARD_FORM_PLACEHOLDER,
  Props
>;

export class CardFormPlaceholder extends Item<
  typeof CARD_FORM_PLACEHOLDER,
  Props
> {
  protected render({
    text = 'Add Card',
    width = '270px',
    height = '380px',
    maxCards = 6,
    cardTemplate
  }: RenderProps<Props>) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const {
      addSubItem,
      sequence,
      editorMode,
      globalPlaceholders,
      globalPlaceholdersLoading,
      globalPlaceholdersError
    } = useEditorStore();

    const existingCardCount = (sequence?.items || []).filter(
      (it: { __typename?: string }) => it?.__typename === 'Card'
    ).length;
    const limitReached = maxCards && existingCardCount >= maxCards;

    const disabledState = getAddCardDisabledState({
      globalPlaceholders,
      globalPlaceholdersLoading,
      globalPlaceholdersError
    });

    const handleOpenModal = () => {
      if (!editorMode || !sequence || limitReached || disabledState.disabled)
        return;

      setIsEditModalOpen(true);
    };

    const handleCreateCard = (formData: Record<string, unknown>) => {
      if (!sequence) return;

      const tempId = `temp-${Date.now()}`;

      const existingCardPositions = (sequence.items || [])
        .filter(
          (item: { __typename?: string; typename?: string }) =>
            item.__typename === 'Card' || item.typename === 'Card'
        )
        .map((item: { position?: unknown }) =>
          typeof item.position === 'number' ? item.position : 0
        );

      const maxPosition =
        existingCardPositions.length > 0
          ? Math.max(...existingCardPositions)
          : 0;

      const position = maxPosition + 1;

      const addSubItemPayload = {
        sequenceId: sequence.id,
        data: {
          type: 'CARD',
          ...formData,
          parent_id: null,
          position
        },
        newItem: {
          id: tempId,
          __typename: 'Card',
          position
        }
      };

      addSubItem(addSubItemPayload);
    };

    const handleCloseEditModal = () => {
      setIsEditModalOpen(false);
    };

    if (!editorMode) {
      return null;
    }

    return (
      <>
        {!limitReached && (
          <AddCardOutline
            width={width}
            height={height}
            text={text}
            onClick={handleOpenModal}
            disabled={disabledState.disabled}
            title={disabledState.reason}
          />
        )}

        {isEditModalOpen && (
          <CardModal
            mode="create"
            open={isEditModalOpen}
            onClose={handleCloseEditModal}
            onCreate={handleCreateCard}
            fields={cardTemplate.fields}
            validation={cardTemplate.validation}
            modalTitle={cardTemplate.createModalTitle}
          />
        )}
      </>
    );
  }
}

registry[CARD_FORM_PLACEHOLDER] = CardFormPlaceholder;
